import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Quote, Check, Lightbulb, X, CircleDot } from "lucide-react";
import { MovesGrid } from "./MoveExplorer";
import InteractiveJournal from "./InteractiveJournal";
import { useProgress } from "../context/ProgressContext";
import { useSettings } from "../context/SettingsContext";
import { youtubeEmbed } from "../lib/video";

const fade = {
  initial: { opacity: 0, y: 18 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.45, ease: "easeOut" },
};

const Reflection = ({ id, prompt }) => {
  const { journal, saveJournal } = useProgress();
  const [val, setVal] = useState("");
  useEffect(() => { setVal(journal[id] || ""); }, [journal, id]);
  return (
    <motion.div {...fade} className="my-8 bg-[#141414] border border-white/10 rounded-lg p-6">
      <div className="flex items-center gap-2 mb-3">
        <Lightbulb size={16} className="text-[#C41E24]" />
        <span className="font-subheadings text-xs font-bold uppercase tracking-widest text-white/70">Player Reflection</span>
      </div>
      <p className="text-white/85 font-semibold mb-4">{prompt}</p>
      <textarea
        data-testid={`reflection-${id}`}
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onBlur={() => saveJournal(id, val)}
        rows={3}
        placeholder="Write your answer here — it saves automatically."
        className="w-full bg-[#0d0d0d] border border-white/10 rounded-md p-3 text-sm text-white placeholder:text-white/30 resize-y focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C41E24]"
      />
    </motion.div>
  );
};

const PersistChecklist = ({ id, title, items }) => {
  const key = `noSoccerCheck_${id}`;
  const [checked, setChecked] = useState({});
  useEffect(() => { setChecked(JSON.parse(localStorage.getItem(key) || "{}")); }, [key]);
  const toggle = (i) => {
    const next = { ...checked, [i]: !checked[i] };
    setChecked(next);
    localStorage.setItem(key, JSON.stringify(next));
  };
  return (
    <motion.div {...fade} className="my-8 bg-[#141414] border border-white/10 rounded-lg p-6">
      {title && <h4 className="font-subheadings text-sm font-bold uppercase tracking-widest text-white/80 mb-4">{title}</h4>}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
        {items.map((item, i) => (
          <button
            key={i}
            data-testid={`checklist-${id}-${i}`}
            onClick={() => toggle(i)}
            className="flex items-center gap-3 text-left group focus-visible:outline-none"
          >
            <span className={`flex-shrink-0 w-5 h-5 rounded-sm border flex items-center justify-center transition-colors ${checked[i] ? "bg-[#C41E24] border-[#C41E24]" : "border-white/30 group-hover:border-[#C41E24]"}`}>
              {checked[i] && <Check size={13} className="text-white" />}
            </span>
            <span className={`text-sm transition-colors ${checked[i] ? "text-white/40 line-through" : "text-white/80"}`}>{item}</span>
          </button>
        ))}
      </div>
    </motion.div>
  );
};

const StaticChecklist = ({ title, items }) => (
  <motion.div {...fade} className="my-8 bg-[#141414] border border-white/10 rounded-lg p-6">
    {title && <h4 className="font-subheadings text-sm font-bold uppercase tracking-widest text-white/80 mb-4">{title}</h4>}
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
      {items.map((item, i) => (
        <div key={i} className="flex items-center gap-3">
          <span className="flex-shrink-0 w-5 h-5 rounded-sm bg-[#C41E24]/15 flex items-center justify-center"><Check size={12} className="text-[#C41E24]" /></span>
          <span className="text-sm text-white/80">{item}</span>
        </div>
      ))}
    </div>
  </motion.div>
);

const ChapterVideo = ({ chapterId, title }) => {
  const { chapterVideos } = useSettings();
  const coachUrl = chapterVideos?.[`${chapterId}:coach`] || chapterVideos?.[chapterId];
  const playerUrl = chapterVideos?.[`${chapterId}:player`];
  const videos = [
    { key: "coach", label: "Watch the Coach", url: coachUrl },
    { key: "player", label: "Watch a Player", url: playerUrl },
  ].filter((v) => v.url);
  if (!videos.length) return null;
  return (
    <motion.figure {...fade} className="my-10" data-testid={`chapter-video-${chapterId}`}>
      <div className="flex items-center gap-2 mb-4">
        <span className="w-6 h-px bg-[#C41E24]" />
        <span className="font-subheadings text-xs font-bold uppercase tracking-widest text-white/70">{title || "Watch: Moves In Action"}</span>
      </div>
      <div className={`grid gap-6 ${videos.length > 1 ? "sm:grid-cols-2" : "grid-cols-1"}`}>
        {videos.map((v) => {
          const embed = youtubeEmbed(v.url);
          return (
            <div key={v.key} data-testid={`chapter-video-${chapterId}-${v.key}`}>
              <div className="font-subheadings text-[11px] font-bold uppercase tracking-widest text-[#C41E24] mb-2">{v.label}</div>
              <div className="rounded-xl overflow-hidden border border-white/10 aspect-video shadow-[0_16px_48px_rgba(0,0,0,0.6)]">
                {embed ? (
                  <iframe className="w-full h-full" src={embed} title={`${title || "Chapter video"} — ${v.label}`} frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen data-testid={`chapter-video-iframe-${chapterId}-${v.key}`} />
                ) : (
                  <a href={v.url} target="_blank" rel="noopener noreferrer" className="w-full h-full flex items-center justify-center bg-[#141414] hover:bg-[#1a1a1a] transition-colors text-sm font-bold uppercase tracking-widest text-white gap-2">
                    ▶ Watch Video
                  </a>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </motion.figure>
  );
};

const Block = ({ block }) => {
  switch (block.type) {
    case "quote":
      return (
        <motion.blockquote {...fade} className="my-10 relative pl-6 border-l-4 border-[#C41E24]">
          <Quote size={28} className="text-[#C41E24]/40 mb-2" />
          <p className="font-subheadings text-xl md:text-2xl font-semibold text-white leading-snug italic">{block.text}</p>
        </motion.blockquote>
      );
    case "h":
      return <motion.h3 {...fade} className="font-headings text-2xl md:text-3xl uppercase tracking-wide text-white mt-12 mb-4">{block.text}</motion.h3>;
    case "p":
      return <motion.p {...fade} className="text-base md:text-lg text-white/65 leading-relaxed my-4 max-w-3xl">{block.text}</motion.p>;
    case "cues":
      return (
        <motion.div {...fade} className="my-8 bg-[#1A1A1A] border border-white/10 rounded-lg p-6">
          {block.title && <h4 className="font-subheadings text-sm font-bold uppercase tracking-widest text-white/80 mb-4">{block.title}</h4>}
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {block.items.map((c, i) => <li key={i} className="text-sm text-white/75 flex gap-2"><CircleDot size={15} className="text-[#C41E24] flex-shrink-0 mt-0.5" />{c}</li>)}
          </ul>
        </motion.div>
      );
    case "mistakes":
      return (
        <motion.div {...fade} className="my-8 bg-[#1A1A1A] border border-white/10 rounded-lg p-6">
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {block.items.map((c, i) => <li key={i} className="text-sm text-white/65 flex gap-2"><X size={15} className="text-[#C41E24] flex-shrink-0 mt-0.5" />{c}</li>)}
          </ul>
        </motion.div>
      );
    case "callout":
      return (
        <motion.div {...fade} className="my-10 bg-gradient-to-br from-[#1A1A1A] to-[#111] border border-white/5 border-l-4 border-l-[#C41E24] rounded-r-lg p-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
          <div className="text-[10px] font-bold uppercase tracking-widest text-[#C41E24] mb-2">{block.title}</div>
          <p className="font-subheadings text-lg md:text-xl font-semibold text-white leading-snug">{block.text}</p>
        </motion.div>
      );
    case "statgrid":
      return (
        <motion.div {...fade} className="my-8 grid gap-4 grid-cols-1 sm:grid-cols-3">
          {block.items.map((s, i) => (
            <div key={i} className="bg-[#141414] border border-white/10 rounded-lg p-6 hover:border-[#C41E24]/40 transition-colors">
              <div className="font-cover text-[#C41E24] text-2xl tracking-wide mb-1">{s.label}</div>
              <div className="font-subheadings font-semibold text-white">{s.value}</div>
            </div>
          ))}
        </motion.div>
      );
    case "purposegrid":
      return (
        <motion.div {...fade} className="my-8 grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
          {block.items.map((s, i) => (
            <div key={i} className="bg-[#141414] border border-white/10 rounded-lg p-5 hover:-translate-y-1 hover:border-[#C41E24]/40 transition-[transform,border-color]">
              <h5 className="font-headings text-lg uppercase tracking-wide text-[#C41E24] mb-2">{s.title}</h5>
              <p className="text-sm text-white/65 leading-relaxed">{s.text}</p>
            </div>
          ))}
        </motion.div>
      );
    case "benchmark":
      return (
        <motion.div {...fade} className="my-8 overflow-x-auto rounded-lg border border-white/10">
          <table className="w-full text-left border-collapse text-sm" data-testid="benchmark-table">
            <thead>
              <tr className="bg-[#1A1A1A]">
                {["Skill", "Bronze", "Silver", "Gold", "Elite"].map((h, i) => (
                  <th key={h} className={`p-4 font-subheadings text-xs font-bold uppercase tracking-widest ${i === 4 ? "text-[#C41E24]" : "text-white/60"}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((r, i) => (
                <tr key={i} className="border-t border-white/10">
                  <td className="p-4 font-semibold text-white">{r.skill}</td>
                  <td className="p-4 text-white/60">{r.bronze}</td>
                  <td className="p-4 text-white/60">{r.silver}</td>
                  <td className="p-4 text-white/60">{r.gold}</td>
                  <td className="p-4 font-bold text-[#C41E24]">{r.elite}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </motion.div>
      );
    case "checklist":
      return block.persist ? <PersistChecklist id={block.id} title={block.title} items={block.items} /> : <StaticChecklist title={block.title} items={block.items} />;
    case "reflection":
      return <Reflection id={block.id} prompt={block.prompt} />;
    case "movesgrid":
      return <MovesGrid tier={block.tier} />;
    case "chapter_video":
      return <ChapterVideo chapterId={block.chapterId} title={block.title} />;
    case "journal_app":
      return <InteractiveJournal />;
    case "image":
      return (
        <motion.figure {...fade} className="my-10">
          <div className="relative overflow-hidden rounded-lg border border-white/10 aspect-[16/9] group">
            <img loading="lazy" decoding="async" src={block.src} alt={block.caption || "Training visual"} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#111111]/70 to-transparent" />
          </div>
          {block.caption && <figcaption className="text-xs text-white/40 uppercase tracking-widest font-semibold mt-3 flex items-center gap-2"><span className="w-6 h-px bg-[#C41E24]" />{block.caption}</figcaption>}
        </motion.figure>
      );
    default:
      return null;
  }
};

export default function ChapterRenderer({ chapter }) {
  return (
    <div>
      {chapter.blocks.map((b, i) => <Block key={i} block={b} />)}
    </div>
  );
}
